/** @typedef {[number, number]} Point */

import { Arrow, Text } from 'react-konva';
import {
  buildDimensionAnnotation,
  estimateTextHalfWidth,
  formatLengthFromPixels,
} from '../lib/floorPlanDesignerDimensions.js';
import {
  DIMENSION_ARROW_TO_TEXT_GAP_PX,
  DIMENSION_WALL_TO_ARROW_GAP_PX,
} from '../lib/floorPlanDesignerConfig.js';

/**
 * @param {{
 *   start: Point;
 *   end: Point;
 *   lengthPx: number;
 *   metersPerPixel: number;
 *   displayScale: number;
 *   sideSign?: 1 | -1;
 *   lineOffsetPx?: number;
 *   fontSize?: number;
 *   text?: string;
 *   annotation?: ReturnType<typeof buildDimensionAnnotation>;
 * }} props
 */
function FloorPlanDesignerDimension({
  start,
  end,
  lengthPx,
  metersPerPixel,
  displayScale,
  sideSign = 1,
  lineOffsetPx,
  fontSize = 11,
  text,
  annotation: annotationOverride,
}) {
  const labelText = text ?? formatLengthFromPixels(lengthPx, metersPerPixel);
  const annotation =
    annotationOverride ??
    buildDimensionAnnotation(start, end, lengthPx, metersPerPixel, displayScale, {
      sideSign,
      wallToArrowGapPx:
        lineOffsetPx != null ? Math.abs(lineOffsetPx) : DIMENSION_WALL_TO_ARROW_GAP_PX,
      arrowToTextGapPx: DIMENSION_ARROW_TO_TEXT_GAP_PX,
      fontSizeBase: fontSize,
    });
  const textOffsetX = estimateTextHalfWidth(labelText, annotation.fontSize);

  return (
    <>
      <Arrow
        points={annotation.linePoints}
        pointerAtBeginning
        pointerAtEnding
        pointerLength={annotation.pointerSize}
        pointerWidth={annotation.pointerSize}
        stroke="#111827"
        fill="#111827"
        strokeWidth={annotation.strokeWidth}
        listening={false}
      />
      <Text
        x={annotation.textX}
        y={annotation.textY}
        text={labelText}
        rotation={annotation.textRotation}
        fontSize={annotation.fontSize}
        fill="#111827"
        align="center"
        offsetX={textOffsetX}
        offsetY={annotation.textOffsetY}
        listening={false}
      />
    </>
  );
}

export default FloorPlanDesignerDimension;
